import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './../index.css';
import FullInventory from '../../../components/Icons/FullInventory';
import PartialInventory from '../../../components/Icons/PartialInventory';
import Inventory from '../../../components/Icons/Inventory';
import InventoryCount from "../../../components/Icons/InventoryCount";
import InventoryId from "../../../components/Icons/InventoryId";
import { DeleteConfirmationModal } from './DeleteConfirmationModal';
import { useDeleteInventoryRequest } from '../../../hooks/useApi';

const InventoryIcon = ({type}) =>
{
  if (type === 'FULL')
  {
    return (
      <FullInventory/>
    );
  }
  return (
    <PartialInventory/>
  );
};

const DeleteIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M2.5 5H4.16667H17.5" stroke="#FF3B30" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <path
      d="M15.8333 5V16.6667C15.8333 17.5 15.1667 18.3333 14.1667 18.3333H5.83333C4.83333 18.3333 4.16667 17.5 4.16667 16.6667V5"
      stroke="#FF3B30" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <path
      d="M6.66667 5V3.33333C6.66667 2.5 7.33333 1.66667 8.33333 1.66667H11.6667C12.6667 1.66667 13.3333 2.5 13.3333 3.33333V5"
      stroke="#FF3B30" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const MoreIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="10" cy="4" r="1.5" fill="#000"/>
    <circle cx="10" cy="10" r="1.5" fill="#000"/>
    <circle cx="10" cy="16" r="1.5" fill="#000"/>
  </svg>
);

export const InventoryItems = ({inventories, loading, error, posId, onInventoryDeleted, hasActiveFilters}) =>
{
  const navigate = useNavigate();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedInventory, setSelectedInventory] = useState(null);
  const [expandedTags, setExpandedTags] = useState({});

  const toggleTags = (e, id) =>
  {
    e.stopPropagation();
    setExpandedTags((prev) => ({ ...prev, [id]: !prev[id] }));
  };
  const [{loading: isDeleting, error: deleteError}, deleteInventory] = useDeleteInventoryRequest(posId);

  const handleInventoryClick = (inventory) =>
  {
    navigate(`/inventory-management/${inventory.id}`, {
      state: {inventory}
    });
  };

  const handleDeleteClick = (e, inventory) =>
  {
    e.stopPropagation(); // Prevent card click
    setSelectedInventory(inventory);
    setIsDeleteModalOpen(true);
  };

  const handleMoreClick = (e) =>
  {
    e.stopPropagation(); // Prevent card click
    // TODO: Implement more actions
  };

  const handleCloseDeleteModal = () =>
  {
    setIsDeleteModalOpen(false);
    setSelectedInventory(null);
  };

  const handleConfirmDelete = async () =>
  {
    if (selectedInventory)
    {
      await deleteInventory({bodyParams: {InventoryId: selectedInventory.id}});

      if (!deleteError)
      {
        handleCloseDeleteModal();
        if (onInventoryDeleted)
        {
          onInventoryDeleted();
        }
      }
    }
  };

  const formatDate = (dateString) =>
  {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {day: '2-digit', month: '2-digit', year: 'numeric'});
  };

  const getStatusColor = (status) =>
  {
    switch (status)
    {
      case 'ACTIVE':
      case 'IN_REVIEW':
        return 'status-active';
      case 'COMPLETED':
        return 'status-completed';
      case 'REJECTED':
        return 'status-rejected';
      default:
        return 'status-default';
    }
  };

  if (loading)
  {
    return <div className="inventory-loading">Loading inventories...</div>;
  }

  if (error)
  {
    return (
      <div className="inventory-error">
        {typeof error === 'string' ? error : 'Error loading inventories'}
      </div>
    );
  }

  if (!loading && inventories.length === 0 && hasActiveFilters)
  {
    return (
      <div className="inventory-empty">
        <p className="inventory-empty-title">No inventories found</p>
        <p className="inventory-empty-subtitle">Try adjusting your filters</p>
      </div>
    );
  }

  return (
    <>
      <div className="inventory-items-container">
        <div className="inventory-grid">
          {inventories.map((inventory, index) => (
            <div
              key={inventory.id || index}
              className="inventory-card"
              onClick={() => handleInventoryClick(inventory)}
              style={{cursor: 'pointer'}}
            >
              {/* Header */}
              <div className="inventory-card-header">
                <div className="inventory-title-section">
                  <div className="inventory-icon">
                    <InventoryIcon type={inventory.type}/>
                  </div>
                  <div className="inventory-title">
                    <span>{inventory.name}</span>
                  </div>
                  <span className={`inventory-status ${getStatusColor(inventory.status)}`}>
                  <span className="status-dot"></span>
                    {inventory.status}
                </span>
                </div>
                <div className="inventory-actions">
                  {!['COMPLETED', 'IN_REVIEW', 'REJECTED'].includes(inventory.status) ?
                    <button
                      className="inventory-action-btn delete-btn"
                      onClick={(e) => handleDeleteClick(e, inventory)}
                    >
                      <DeleteIcon/>
                    </button> : ''}
                  {/* <button
                    className="inventory-action-btn more-btn"
                    onClick={handleMoreClick}
                  >
                    <MoreIcon/>
                  </button> */}
                </div>
              </div>

              {/* Stats */}
              <div className="inventory-stats-grid">
                <div className="inventory-stat-item">
                  <span className="stat-label">Session</span>
                  <span className="stat-value">{inventory.totalSessions || 0}</span>
                </div>
                <div className="inventory-stat-item">
                  <span className="stat-label">Scanned</span>
                  <span className="stat-value">{inventory.scannedQuantity || 0}</span>
                </div>
                <div className="inventory-stat-item">
                  <span className="stat-label">Discrepancies</span>
                  <span className="stat-value">{inventory.numberOfDiscrepancies || 0}</span>
                </div>
              </div>

              {/* Details */}
              <div className="inventory-details">
                <div className="inventory-detail-row">
                  <span className="detail-icon"><Inventory width={16} height={16}/></span>
                  <span className="detail-text">{inventory.type === 'FULL' ? 'Full' : 'Partial'} Inventory</span>
                </div>
                <div className="inventory-detail-row">
                  <span className="detail-icon"><InventoryId/></span>
                  <span className="detail-text">Inventory ID: {inventory.id}</span>
                </div>
                <div className="inventory-detail-row">
                  <span className="detail-icon"><InventoryCount/></span>
                  <span className="detail-text">Inventory Count: {inventory.expectedQuantity || '-'}</span>
                </div>
              </div>

              {/* Filters/Tags */}
              {inventory.filters && inventory.filters.length > 0 && (
                <div className="inventory-tags">
                  {(expandedTags[inventory.id] ? inventory.filters : inventory.filters.slice(0, 3)).map((filter, idx) => (
                    <span key={idx} className="inventory-tag">
                      {filter.type} : {filter.value}
                    </span>
                  ))}
                  {inventory.filters.length > 3 && (
                    <button
                      className="inventory-tags-toggle"
                      onClick={(e) => toggleTags(e, inventory.id)}
                    >
                      {expandedTags[inventory.id] ? 'Show less' : `Show more +${inventory.filters.length - 3}`}
                    </button>
                  )}
                </div>
              )}

              {/* Footer */}
              <div className="inventory-footer">
                <div className="footer-item">
                <span className="footer-label">
                  Created: {formatDate(inventory.createdOn)}
                </span>
                  <span className="footer-user">
                  by {inventory.createdBy}
                </span>
                </div>
                {inventory.status === 'COMPLETED' && inventory.createdOn && (
                  <div className="footer-item">
                    <span className="footer-label">Completed: {formatDate(inventory.completedOn)}</span>
                    <span className="footer-user">By {inventory.completedBy}</span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={handleCloseDeleteModal}
        onConfirm={handleConfirmDelete}
        inventoryName={selectedInventory?.name || ''}
        isDeleting={isDeleting}
      />
    </>
  );
};